/**
 * Which leads Terac may cold-email, decided by the recipient's country: its
 * anti-spam law is the one that applies. Every country sits in one tier:
 *
 * - ALLOW: an opt-out regime; any address may be emailed.
 * - WORK_ONLY: a named work address may be emailed with identification and an
 *   opt-out; a personal (free-mail) address needs consent, which we never hold.
 * - BLOCK: prior consent is needed even at work, so no cold email at all.
 *
 * A country missing from the table is blocked: we only send where the law has
 * been checked. Moving a country is an edit to its entry below.
 */

import { isConsumerMailboxDomain } from "./consumer-mailbox-domains";
import { normalizeFullIsoAlpha2 } from "./countries";
import { matchLocationCountry } from "./location-country";

export type OutboundJurisdictionTier = "ALLOW" | "WORK_ONLY" | "BLOCK";

interface Jurisdiction {
  tier: OutboundJurisdictionTier;
  /** The statute, cited in every withhold reason. */
  law: string;
  /** What the law requires, in one line. */
  basis: string;
  /** A change of tier already enacted, applied from `from`. */
  change?: { from: Date; tier: OutboundJurisdictionTier; basis: string };
}

/** DPDP Rules Phase III: midnight at the start of 2027-05-13 in India. */
const DPDP_PHASE_III = new Date("2027-05-13T00:00:00+05:30");

const OUTBOUND_JURISDICTIONS: Record<string, Jurisdiction> = {
  US: {
    tier: "ALLOW",
    law: "CAN-SPAM Act",
    basis: "opt-out; identification and postal address in every email",
  },
  BR: {
    tier: "ALLOW",
    law: "LGPD art. 7(IX)",
    basis: "legitimate interest with an opt-out",
  },
  IN: {
    tier: "ALLOW",
    law: "DPDP Act 2023 s.6",
    basis: "no consent duty before DPDP Phase III",
    change: {
      from: DPDP_PHASE_III,
      tier: "BLOCK",
      basis: "consent required from DPDP Phase III",
    },
  },

  GB: {
    tier: "WORK_ONLY",
    law: "PECR reg. 22",
    basis: "opt-out for corporate subscribers; individuals need consent",
  },
  IE: {
    tier: "WORK_ONLY",
    law: "S.I. 336/2011 reg. 13",
    basis: "opt-out for addresses used for business",
  },
  FR: {
    tier: "WORK_ONLY",
    law: "CPCE L34-5",
    basis: "opt-out for work addresses when relevant to the role",
  },
  SE: {
    tier: "WORK_ONLY",
    law: "MFL 2008:486 §19",
    basis: "consent for natural persons; employees unsettled",
  },
  FI: {
    tier: "WORK_ONLY",
    law: "Act 917/2014 §200",
    basis: "opt-out only when the offer fits the person's duties",
  },
  PT: {
    tier: "WORK_ONLY",
    law: "Lei 41/2004 art. 13-A",
    basis: "unsettled whether employees must consent",
  },

  DE: {
    tier: "BLOCK",
    law: "UWG §7(2)",
    basis: "prior consent for any addressee",
  },
  AT: {
    tier: "BLOCK",
    law: "TKG 2021 §174",
    basis: "prior consent, and asking for it by email is barred",
  },
  DK: {
    tier: "BLOCK",
    law: "Markedsføringsloven §10",
    basis: "prior consent from the employee personally",
  },
  IT: {
    tier: "BLOCK",
    law: "Codice privacy art. 130",
    basis: "prior consent, market research included",
  },
  ES: {
    tier: "BLOCK",
    law: "LSSI art. 21",
    basis: "prior consent, legal persons included",
  },
  NL: {
    tier: "BLOCK",
    law: "Telecommunicatiewet 11.7",
    basis: "prior consent unless the address was published for offers",
  },
  BE: {
    tier: "BLOCK",
    law: "CDE XII.13",
    basis: "prior consent for named people",
  },
  PL: {
    tier: "BLOCK",
    law: "PKE art. 398",
    basis: "prior consent, companies included",
  },
  CZ: { tier: "BLOCK", law: "Act 480/2004 §7", basis: "prior consent" },
  NO: {
    tier: "BLOCK",
    law: "Markedsføringsloven §15",
    basis: "prior consent for individual work addresses",
  },
  CH: {
    tier: "BLOCK",
    law: "UWG art. 3(1)(o)",
    basis: "prior consent for mass advertising",
  },
  CA: {
    tier: "BLOCK",
    law: "CASL s.6",
    basis: "prior consent; implied only with evidence of self-publication",
  },
  AU: {
    tier: "BLOCK",
    law: "Spam Act 2003 s.16",
    basis: "prior consent; inferred only with evidence of self-publication",
  },
  SG: {
    tier: "BLOCK",
    law: "Spam Control Act 2007",
    basis: "bulk email needs an <ADV> subject label",
  },
  NZ: {
    tier: "BLOCK",
    law: "UEM Act 2007 s.9",
    basis: "prior consent; deemed only for published role addresses",
  },
  JP: {
    tier: "BLOCK",
    law: "Specified E-mail Act art. 3",
    basis: "prior opt-in consent",
  },
  KR: {
    tier: "BLOCK",
    law: "Network Act art. 50",
    basis: "prior opt-in consent",
  },
  ZA: {
    tier: "BLOCK",
    law: "POPIA s.69",
    basis: "prior consent for electronic direct marketing",
  },
};

/**
 * Country-code TLDs sold as generic names: Google's list of generic ccTLDs plus
 * the ones startups use the same way. They name a country only under a registry
 * second level, as in "empresa.com.co".
 */
const GENERIC_USE_CCTLDS: Record<string, true> = {
  ad: true,
  ai: true,
  as: true,
  bz: true,
  cc: true,
  cd: true,
  co: true,
  dj: true,
  fm: true,
  gg: true,
  io: true,
  la: true,
  ly: true,
  me: true,
  ms: true,
  nu: true,
  sc: true,
  sh: true,
  so: true,
  sr: true,
  su: true,
  tk: true,
  to: true,
  tv: true,
  vc: true,
  ws: true,
};

const REGISTRY_SECOND_LEVELS: Record<string, true> = {
  ac: true,
  co: true,
  com: true,
  edu: true,
  gov: true,
  net: true,
  org: true,
};

export interface OutboundJurisdictionLead {
  email: string;
  location: string | null;
}

function countryFromEmailDomain(email: string): string | null {
  const domain = email.trim().toLowerCase().split("@")[1] ?? "";
  const labels = domain.replace(/\.$/, "").split(".");
  const tld = labels[labels.length - 1] ?? "";
  if (labels.length < 2 || tld.length !== 2) {
    return null;
  }
  if (tld === "uk") {
    return "GB";
  }
  const secondLevel = labels[labels.length - 2] ?? "";
  if (GENERIC_USE_CCTLDS[tld] && !REGISTRY_SECOND_LEVELS[secondLevel]) {
    return null;
  }
  return normalizeFullIsoAlpha2(tld);
}

/**
 * The country whose law governs emailing this lead, or null when nothing
 * identifies one. A named location wins over the email domain's ccTLD; a bare
 * two-letter code ("Berlin, DE" reads as Delaware) is only used when the domain
 * says nothing.
 */
export function resolveLeadCountry(
  lead: OutboundJurisdictionLead,
): string | null {
  const fromLocation = lead.location
    ? matchLocationCountry(lead.location)
    : null;
  if (fromLocation && !fromLocation.weak) {
    return fromLocation.country;
  }
  return countryFromEmailDomain(lead.email) ?? fromLocation?.country ?? null;
}

/** Why this lead must not be cold-emailed at `now`, or null when it may be. */
export function outboundJurisdictionWithholdReason(
  lead: OutboundJurisdictionLead,
  now: Date,
): string | null {
  const country = resolveLeadCountry(lead);
  // No usable location and no ccTLD: allowed, as that is the most the data
  // supports. An unidentified EU recipient is an accepted residual risk.
  if (!country) {
    return null;
  }
  const entry = OUTBOUND_JURISDICTIONS[country];
  if (!entry) {
    return `${country}: not assessed for cold email`;
  }
  const { tier, law, basis } =
    entry.change && now.getTime() >= entry.change.from.getTime()
      ? { ...entry, ...entry.change }
      : entry;

  switch (tier) {
    case "ALLOW":
      return null;
    case "WORK_ONLY":
      return isConsumerMailboxDomain(lead.email.split("@")[1] ?? "")
        ? `${country}: personal address needs consent (${law})`
        : null;
    case "BLOCK":
      return `${country}: ${basis} (${law})`;
  }
}
